import { intlTelInput } from '@intl-tel-input/react';

export const DEFAULT_COUNTRY = 'BR';

/** Strips everything but digits. */
export function onlyDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

// --- Document (CPF / CNPJ) -------------------------------------------------

/** Masks a CPF as the user types: 000.000.000-00 (11 digits). */
export function maskCpf(value) {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length > 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length > 6) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  if (d.length > 3) return `${d.slice(0, 3)}.${d.slice(3)}`;
  return d;
}

/** Masks a CNPJ as the user types: 00.000.000/0000-00 (14 digits). */
export function maskCnpj(value) {
  const d = onlyDigits(value).slice(0, 14);
  if (d.length > 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  if (d.length > 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  if (d.length > 5) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length > 2) return `${d.slice(0, 2)}.${d.slice(2)}`;
  return d;
}

/** Masks a document as CPF (<= 11 digits) or CNPJ (> 11). */
export function maskDocument(value) {
  return onlyDigits(value).length > 11 ? maskCnpj(value) : maskCpf(value);
}

// --- CEP ------------------------------------------------------------------

/** Masks a Brazilian ZIP code as the user types: 00000-000 (8 digits). */
export function maskZipCode(value) {
  const d = onlyDigits(value).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

// --- Birth date -----------------------------------------------------------

/** Masks a date as the user types: DD/MM/AAAA (8 digits). */
export function maskDate(value) {
  const d = onlyDigits(value).slice(0, 8);
  if (d.length > 4) return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
  if (d.length > 2) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return d;
}

/** Converts DD/MM/AAAA to AAAA-MM-DD, or null when it is not a real calendar date. */
export function toIsoDate(value) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value || '').trim());
  if (!match) return null;
  const [, day, month, year] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (date.getUTCFullYear() !== Number(year) || date.getUTCMonth() !== Number(month) - 1 || date.getUTCDate() !== Number(day)) {
    return null;
  }
  return `${year}-${month}-${day}`;
}

/** Validates a birth date typed as DD/MM/AAAA: a real date, not in the future and after 1900. */
export function isValidBirthDate(value) {
  const iso = toIsoDate(value);
  if (!iso) return false;
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return iso >= '1900-01-01' && iso <= todayIso;
}

/** 'Cpf' | 'Cnpj' | null, based on digit count. */
export function documentType(value) {
  const len = onlyDigits(value).length;
  if (len === 11) return 'Cpf';
  if (len === 14) return 'Cnpj';
  return null;
}

/** Validates a CPF via the check-digit algorithm. */
export function isValidCpf(value) {
  const d = onlyDigits(value);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const digit = (length) => {
    let sum = 0;
    for (let i = 0; i < length; i += 1) sum += Number(d[i]) * (length + 1 - i);
    const mod = (sum * 10) % 11;
    return mod === 10 ? 0 : mod;
  };
  return digit(9) === Number(d[9]) && digit(10) === Number(d[10]);
}

/** Validates a CNPJ via the check-digit algorithm. */
export function isValidCnpj(value) {
  const d = onlyDigits(value);
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const digit = (length) => {
    const weights = length === 12
      ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
      : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < length; i += 1) sum += Number(d[i]) * weights[i];
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };
  return digit(12) === Number(d[12]) && digit(13) === Number(d[13]);
}

/** Validates a document as either CPF or CNPJ. */
export function isValidDocument(value) {
  const len = onlyDigits(value).length;
  if (len === 11) return isValidCpf(value);
  if (len === 14) return isValidCnpj(value);
  return false;
}

// --- Phone (intl-tel-input) -----------------------------------------------

function dialCodeOf(country) {
  const iso2 = String(country || DEFAULT_COUNTRY).toLowerCase();
  return intlTelInput.getAllCountries().find((c) => c.iso2 === iso2)?.dialCode ?? '55';
}

function dialCodeFromE164(digits, country) {
  const selected = dialCodeOf(country);
  if (digits.startsWith(selected)) return selected;
  return intlTelInput.getAllCountries()
    .map((c) => c.dialCode)
    .filter((code) => digits.startsWith(code))
    .sort((a, b) => b.length - a.length)[0] ?? selected;
}

/** Validates a phone number for the given country (accepts +<code> too). */
export function isValidPhone(value, country = DEFAULT_COUNTRY) {
  const v = String(value || '').trim();
  if (!v) return false;
  const utils = intlTelInput.utils;
  if (utils) return utils.isValidNumber(v, String(country || DEFAULT_COUNTRY).toLowerCase());
  return /^\+?\d{8,15}$/.test(v.replace(/[\s()-]/g, ''));
}

/** Splits a phone into { countryCode, areaCode, number } for the API payload. */
export function parsePhoneParts(value, country = DEFAULT_COUNTRY) {
  const raw = String(value || '').trim();
  const digits = onlyDigits(raw);
  const isE164 = raw.startsWith('+');
  const callingCode = isE164 ? dialCodeFromE164(digits, country) : dialCodeOf(country);
  const national = isE164 ? digits.slice(callingCode.length) : digits;
  const isBr = callingCode === '55';
  return {
    countryCode: callingCode,
    areaCode: isBr ? national.slice(0, 2) : '',
    number: isBr ? national.slice(2) : national
  };
}

// --- Money -------------------------------------------------------------------

/** Formats an amount in centavos as a pt-BR reais string (without the R$ prefix). */
export function centsToReais(cents, options = {}) {
  return ((cents || 0) / 100).toLocaleString('pt-BR', options);
}
